import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Youtube } from "lucide-react";
import Logo from "./Logo";
import { useLanguage } from "../contexts/LanguageContext";
import { CONTACT } from "../data/site";

const socials = [
  { icon: Facebook, label: "Facebook", href: "#" },
  { icon: Youtube, label: "YouTube", href: "#" },
];

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] text-white">
      <div className="gk-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo variant="light" size={96} textSize="xl" />
            <p className="mt-6 text-white/70 text-sm max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="flex gap-3 mt-6">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="h-10 w-10 rounded-full bg-white/5 hover:bg-[#14803C] flex items-center justify-center transition-colors border border-white/10"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading font-bold text-sm uppercase tracking-[0.2em] text-[#14803C] mb-4">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/about" className="hover:text-white">{t.nav.about}</Link></li>
              <li><Link to="/impact" className="hover:text-white">{t.nav.impact}</Link></li>
              <li><Link to="/programs" className="hover:text-white">{t.nav.programs}</Link></li>
              <li><Link to="/leadership" className="hover:text-white">{t.nav.leadership}</Link></li>
              <li><Link to="/membership" className="hover:text-white">{t.nav.membership}</Link></li>
              <li><Link to="/get-involved" className="hover:text-white">{t.nav.getInvolved}</Link></li>
              <li><Link to="/contact" className="hover:text-white">{t.nav.contact}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-heading font-bold text-sm uppercase tracking-[0.2em] text-[#14803C] mb-4">
              {t.footer.contactInfo}
            </h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex gap-3">
                <Mail size={16} className="text-[#B5651D] mt-0.5 flex-shrink-0" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white" data-testid="footer-email">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="text-[#B5651D] mt-0.5 flex-shrink-0" />
                <a href={`tel:${CONTACT.phone1.replace(/[^+\d]/g, "")}`} className="hover:text-white" data-testid="footer-phone-1">
                  {CONTACT.phone1}
                </a>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="text-[#B5651D] mt-0.5 flex-shrink-0" />
                <a href={`tel:${CONTACT.phone2.replace(/[^+\d]/g, "")}`} className="hover:text-white" data-testid="footer-phone-2">
                  {CONTACT.phone2}
                </a>
              </li>
              <li className="flex gap-3">
                <MapPin size={16} className="text-[#B5651D] mt-0.5 flex-shrink-0" />
                <span>United States · Africa · Europe</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
            <p>© {new Date().getFullYear()} Grand Kasaï Nsanga Lubangu Corporation. {t.footer.rights}</p>
            <p>{t.footer.governance}</p>
          </div>
          <div className="text-center text-xs text-white/40 pt-2" data-testid="footer-mtbl-credit">
            Made by{" "}
            <span className="font-heading font-bold tracking-[0.2em] text-[#14803C]">
              MTBL Group
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
